import { useCallback, useState } from 'react';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import WarningIcon from '@material-ui/icons/Warning';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { spassuCodeApiRef } from '../../api';
import { useProjectRole } from '../../hooks/useProjectRole';
import { RoleBadge } from '../RoleBadge';
import { AddMemberDialog } from '../AddMemberDialog';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  entityCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tempWarning: {
    color: theme.palette.warning.main,
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
}));

export interface ProjectMembersPanelProps {
  projectKey: string;
  /** Provider de origem do projeto — repassado ao AddMemberDialog para que a
   *  lista de repositórios use o endpoint multi-provider correto. */
  providerId?: import('../../api/types').ProviderId;
}

export const ProjectMembersPanel = ({
  projectKey,
  providerId,
}: ProjectMembersPanelProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const { hasPermission } = useProjectRole();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { value: members, loading } = useAsync(
    () => api.getProjectMembers(projectKey),
    [projectKey, refreshKey],
  );

  const canManage = hasPermission(projectKey, 'member.add');
  const canRemove = hasPermission(projectKey, 'member.remove');

  const handleRefresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const handleRemove = useCallback(
    async (entityRef: string, role: string, repositoryKey: string | null) => {
      const target = repositoryKey
        ? `${entityRef} (${repositoryKey})`
        : entityRef;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Remover ${target} do projeto?`)) return;
      try {
        await api.removeProjectMember(
          projectKey,
          entityRef,
          role as any,
          repositoryKey,
        );
        handleRefresh();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        // eslint-disable-next-line no-alert
        window.alert(`Erro ao remover membro: ${msg}`);
      }
    },
    [api, projectKey, handleRefresh],
  );

  const isExpiringSoon = (expiresAt: string | null): boolean => {
    if (!expiresAt) return false;
    const diff = new Date(expiresAt).getTime() - Date.now();
    return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000; // 7 days
  };

  if (loading) return <Typography>Carregando membros...</Typography>;

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="h6">
          Membros do Projeto ({members?.length ?? 0})
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Adicionar
          </Button>
        )}
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Membro</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Repositórios</TableCell>
            <TableCell>Desde</TableCell>
            <TableCell>Expiração</TableCell>
            {canRemove && <TableCell align="right">Ações</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {members?.map(member => (
            <TableRow key={member.id}>
              <TableCell>
                <div className={classes.entityCell}>
                  {member.entityKind === 'Group' ? (
                    <Tooltip title="Grupo">
                      <GroupIcon fontSize="small" />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Usuário">
                      <PersonIcon fontSize="small" />
                    </Tooltip>
                  )}
                  <span>{member.entityRef}</span>
                </div>
              </TableCell>
              <TableCell>
                <RoleBadge role={member.role} />
              </TableCell>
              <TableCell>{member.repositoryKey ?? 'Todos'}</TableCell>
              <TableCell>
                {new Date(member.createdAt).toLocaleDateString('pt-BR')}
              </TableCell>
              <TableCell>
                {member.expiresAt ? (
                  <span>
                    {new Date(member.expiresAt).toLocaleDateString('pt-BR')}
                    {isExpiringSoon(member.expiresAt) && (
                      <span className={classes.tempWarning}>
                        <WarningIcon style={{ fontSize: 14 }} />
                        Expira em breve!
                      </span>
                    )}
                  </span>
                ) : (
                  '—'
                )}
              </TableCell>
              {canRemove && (
                <TableCell align="right">
                  <Tooltip title="Remover membro">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleRemove(
                          member.entityRef,
                          member.role,
                          member.repositoryKey,
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
          {(!members || members.length === 0) && (
            <TableRow>
              <TableCell colSpan={canRemove ? 6 : 5} align="center">
                <Typography variant="body2" color="textSecondary">
                  Nenhum membro configurado neste projeto. Clique em "Adicionar"
                  para começar.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <AddMemberDialog
        open={addDialogOpen}
        projectKey={projectKey}
        providerId={providerId}
        onClose={() => setAddDialogOpen(false)}
        onAdded={handleRefresh}
      />
    </div>
  );
};
